import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Badge,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import type { StorefrontCartAddedLocationState } from '../../types/chapterSupplies';
import { useApiGet } from '../../utils/httpClientTs';
import {
  getStorefrontCategories,
  listStorefrontItems,
} from '../../endpoints/chapterSupplies';
import type {
  CategoryResponse,
  OnlineItemResponse,
  SubCategoryResponse,
} from '../../types/chapterSupplies';
import {
  addItemToCart,
  getCartCount,
  storefrontItemNeedsConfiguration,
} from '../../utils/chapterSuppliesCart';

const PAGE_SIZE = 24;

const formatPrice = (v: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
    v || 0
  );

const firstImage = (item: OnlineItemResponse): string | undefined =>
  item.images?.slice().sort((a, b) => a.sort_order - b.sort_order)[0]
    ?.image_url;

const sortedVariants = (item: OnlineItemResponse) =>
  [...(item.variants ?? [])].sort((a, b) => a.sort_order - b.sort_order);

/** List card image: prefer any variant image when the product uses variants (matches detail fallback idea). */
const listCardImage = (item: OnlineItemResponse): string | undefined => {
  if ((item.has_variants ?? 0) !== 0) {
    const url = sortedVariants(item)
      .map((v) => v.item_image_url?.trim())
      .find(Boolean);
    if (url) return url;
  }
  return firstImage(item);
};

/** When `has_variants`, derive label from variant prices; otherwise parent `price`. */
const storefrontListPriceLabel = (item: OnlineItemResponse): string => {
  const hasVariants = (item.has_variants ?? 0) !== 0;
  const vars = sortedVariants(item);
  if (hasVariants && vars.length > 0) {
    const prices = vars
      .map((v) => v.price)
      .filter((p) => typeof p === 'number' && !Number.isNaN(p));
    if (prices.length === 0) return formatPrice(item.price ?? 0);
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    if (min === max) return formatPrice(min);
    return `${formatPrice(min)} – ${formatPrice(max)}`;
  }
  return formatPrice(item.price ?? 0);
};

const StorefrontList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const cartAdded = (location.state as StorefrontCartAddedLocationState | null)
    ?.storefrontCartAdded;
  const [searchTerm, setSearchTerm] = useState(
    searchParams.get('search') || ''
  );
  const [categoryID, setCategoryID] = useState(
    searchParams.get('category') || ''
  );
  const [subCategoryID, setSubCategoryID] = useState(
    searchParams.get('subcategory') || ''
  );
  const [page, setPage] = useState(() => {
    const parsed = Number(searchParams.get('page') || 1);
    return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 1;
  });
  const [cartCount, setCartCount] = useState(() => getCartCount());

  const dismissCartAddedBanner = () => {
    navigate('.', { replace: true, state: {} });
  };

  const goToCartFromBanner = () => {
    navigate('/chapter-supplies/storefront/cart');
  };

  const { data: categories = [] } = useApiGet<CategoryResponse[]>(
    ['chapter-supplies-storefront-categories'],
    getStorefrontCategories
  );

  const selectedCategory = useMemo(
    () => categories.find((c) => c.id === categoryID),
    [categories, categoryID]
  );
  const subCategoryOptions = useMemo<SubCategoryResponse[]>(
    () => selectedCategory?.subcategories ?? [],
    [selectedCategory]
  );

  React.useEffect(() => {
    const nextSearch = searchParams.get('search') || '';
    const nextCategory = searchParams.get('category') || '';
    const nextSubCategory = searchParams.get('subcategory') || '';
    const parsedPage = Number(searchParams.get('page') || 1);
    const nextPage =
      Number.isFinite(parsedPage) && parsedPage > 0
        ? Math.floor(parsedPage)
        : 1;
    setSearchTerm((prev) => (prev === nextSearch ? prev : nextSearch));
    setCategoryID((prev) => (prev === nextCategory ? prev : nextCategory));
    setSubCategoryID((prev) =>
      prev === nextSubCategory ? prev : nextSubCategory
    );
    setPage((prev) => (prev === nextPage ? prev : nextPage));
  }, [searchParams]);

  React.useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (searchTerm.trim()) next.set('search', searchTerm.trim());
    else next.delete('search');
    if (categoryID) next.set('category', categoryID);
    else next.delete('category');
    if (subCategoryID) next.set('subcategory', subCategoryID);
    else next.delete('subcategory');
    if (page > 1) next.set('page', String(page));
    else next.delete('page');
    const current = searchParams.toString();
    const target = next.toString();
    if (current !== target) setSearchParams(next, { replace: true });
  }, [
    searchTerm,
    categoryID,
    subCategoryID,
    page,
    searchParams,
    setSearchParams,
  ]);

  React.useEffect(() => {
    if (!subCategoryID) return;
    const isValid = subCategoryOptions.some((s) => s.id === subCategoryID);
    if (!isValid) setSubCategoryID('');
  }, [subCategoryID, subCategoryOptions]);

  const { data, isLoading, error } = useApiGet(
    [
      'chapter-supplies-storefront-list',
      searchTerm,
      categoryID,
      subCategoryID,
      page,
    ],
    () =>
      listStorefrontItems({
        search: searchTerm || undefined,
        category_id: categoryID || undefined,
        sub_category_id: subCategoryID || undefined,
        page,
        page_size: PAGE_SIZE,
      })
  );

  const total = data?.pagination?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 1 }}>
        Emblem / Chapter Supplies
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Use the Categories and Subcategories dropdowns to narrow your search.
        Alternatively, you can type in a keyword in the Search field.
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Online orders can only be made by credit or debit card. If you prefer to
        pay by check and/or with a paper order form, forms can be downloaded
        from the Library on the P.E.O. Hub.
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        P.E.O. jewelry, other than Emblems, must be ordered from Herff Jones.
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          <Button
            variant="outlined"
            onClick={() => navigate('/chapter-supplies/storefront/orders')}
          >
            My Orders
          </Button>
          <Button
            variant="outlined"
            startIcon={
              <Badge color="primary" badgeContent={cartCount}>
                <ShoppingCartIcon />
              </Badge>
            }
            onClick={() => navigate('/chapter-supplies/storefront/cart')}
          >
            My Cart
          </Button>
        </Stack>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            size="small"
            label="Search"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Category</InputLabel>
            <Select
              value={categoryID}
              label="Category"
              onChange={(e) => {
                const nextCategoryID = String(e.target.value);
                setCategoryID(nextCategoryID);
                setSubCategoryID('');
                setPage(1);
              }}
            >
              <MenuItem value="">All</MenuItem>
              {categories.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Subcategory</InputLabel>
            <Select
              value={subCategoryID}
              label="Subcategory"
              disabled={!categoryID}
              onChange={(e) => {
                setSubCategoryID(String(e.target.value));
                setPage(1);
              }}
            >
              <MenuItem value="">All</MenuItem>
              {subCategoryOptions.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {cartAdded && (
        <Alert
          severity="success"
          variant="filled"
          sx={{ mb: 2 }}
          onClose={dismissCartAddedBanner}
          action={
            <Button color="inherit" size="small" onClick={goToCartFromBanner}>
              View cart
            </Button>
          }
        >
          <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
            Added to cart
          </Typography>
          {': '}
          {cartAdded.itemName}
          {cartAdded.variantLabel ? ` (${cartAdded.variantLabel})` : ''}
          {cartAdded.quantity != null && cartAdded.quantity > 1
            ? ` × ${cartAdded.quantity}`
            : ''}
        </Alert>
      )}

      {isLoading && <Typography>Loading storefront items...</Typography>}
      {error && (
        <Typography color="error">
          Unable to load chapter supplies storefront items.
        </Typography>
      )}

      {!isLoading && !error && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {total} item{total === 1 ? '' : 's'} found
          </Typography>
          <Grid container spacing={2}>
            {data?.data?.map((item) => {
              const imageURL = listCardImage(item);
              return (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  lg={3}
                  key={item.id}
                  sx={{ display: 'flex', minWidth: 0 }}
                >
                  <Card sx={{ width: '100%', display: 'flex', minWidth: 0 }}>
                    <CardActionArea
                      onClick={() =>
                        navigate(
                          `/chapter-supplies/storefront/items/${item.id}`
                        )
                      }
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        width: '100%',
                        minWidth: 0,
                      }}
                    >
                      {imageURL ? (
                        <Box
                          component="img"
                          src={imageURL}
                          alt={item.name || item.item_name || 'Storefront item'}
                          sx={{
                            width: '100%',
                            height: 180,
                            objectFit: 'contain',
                            bgcolor: 'grey.100',
                            p: 1,
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: '100%',
                            height: 180,
                            bgcolor: 'grey.100',
                          }}
                        />
                      )}
                      <CardContent
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          flexGrow: 1,
                          width: '100%',
                          minWidth: 0,
                          boxSizing: 'border-box',
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{
                            mb: 0.5,
                            minHeight: 56,
                            width: '100%',
                            maxWidth: '100%',
                            whiteSpace: 'normal',
                            overflowWrap: 'break-word',
                            wordBreak: 'normal',
                            pr: 1,
                            boxSizing: 'border-box',
                          }}
                        >
                          {item.name || item.item_name || 'Untitled Item'}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mb: 1,
                            minHeight: 40,
                            width: '100%',
                            maxWidth: '100%',
                            whiteSpace: 'normal',
                            overflowWrap: 'break-word',
                            wordBreak: 'normal',
                            pr: 1,
                            boxSizing: 'border-box',
                          }}
                        >
                          {item.short_description ||
                            item.long_description ||
                            'No description'}
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ mb: 1, flexWrap: 'wrap', rowGap: 0.5 }}
                        >
                          {item.order_approval_required === 1 && (
                            <Chip
                              size="small"
                              color="warning"
                              label="Approval Required"
                            />
                          )}
                          {item.is_featured === 1 && (
                            <Chip
                              size="small"
                              color="primary"
                              label="Featured"
                            />
                          )}
                        </Stack>
                        <Typography variant="h6" color="primary.main">
                          {storefrontListPriceLabel(item)}
                        </Typography>
                        {storefrontItemNeedsConfiguration(item) ? (
                          <Button
                            size="small"
                            variant="outlined"
                            sx={{ mt: 'auto', alignSelf: 'flex-start' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(
                                `/chapter-supplies/storefront/items/${item.id}`
                              );
                            }}
                          >
                            Select options
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            variant="contained"
                            sx={{ mt: 'auto', alignSelf: 'flex-start' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              addItemToCart(item, {
                                quantity: 1,
                                unit_price: item.price ?? 0,
                                image_url: imageURL,
                                parent_image_url: firstImage(item),
                                child_image_url: sortedVariants(item)
                                  .map((v) => v.item_image_url?.trim())
                                  .find(Boolean),
                              });
                              setCartCount(getCartCount());
                              navigate('.', {
                                replace: true,
                                state: {
                                  storefrontCartAdded: {
                                    itemName:
                                      item.name || item.item_name || 'Item',
                                    quantity: 1,
                                  },
                                } satisfies StorefrontCartAddedLocationState,
                              });
                            }}
                          >
                            Add to Cart
                          </Button>
                        )}
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
          {pageCount > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={pageCount}
                page={page}
                onChange={(_, v) => setPage(v)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default StorefrontList;
